// FU-POS-6c-1 — the §9 "offline — actions queued" surface.
//
// Shows a slim banner whenever the terminal is offline OR has POS mutations still
// waiting to sync, and owns the reconnect drain: it drains on the browser `online`
// event and on a short poll (the seconds–minutes blip target). The queue + server
// idempotency guarantee each queued action applies at most once on replay.
import React, { useEffect, useState } from 'react'
import type { ApiClient } from '../../api/client'
import { offlineQueue } from '../../api/offline-queue'

const RETRY_MS = 15_000

export function OfflineBanner({
  client,
  restaurantId,
}: {
  client: ApiClient
  restaurantId: string
}) {
  const [online, setOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  )
  const [queued, setQueued] = useState(() => offlineQueue.sizeFor(restaurantId))

  useEffect(() => {
    const refresh = () => setQueued(offlineQueue.sizeFor(restaurantId))
    const unsub = offlineQueue.subscribe(refresh)

    const drain = () => {
      void client.drainQueue().then(refresh)
    }
    const goOnline = () => {
      setOnline(true)
      drain()
    }
    const goOffline = () => setOnline(false)

    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    const iv = window.setInterval(() => {
      if (typeof navigator === 'undefined' || navigator.onLine) drain()
    }, RETRY_MS)

    // Mounted possibly already-reconnected with a backlog → attempt once now.
    drain()

    return () => {
      unsub()
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
      window.clearInterval(iv)
    }
  }, [client, restaurantId])

  if (online && queued === 0) return null

  const syncing = online && queued > 0
  const message = syncing
    ? `Reconnecting — syncing ${queued} saved action${queued === 1 ? '' : 's'}…`
    : queued > 0
      ? `You're offline — ${queued} action${queued === 1 ? '' : 's'} saved, will sync automatically.`
      : "You're offline — your actions are being saved and will sync when the connection returns."

  return (
    <div role="status" aria-live="polite" style={{ ...styles.bar, background: syncing ? '#0f766e' : '#b45309' }}>
      <span style={styles.dot} />
      {message}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.4rem 0.9rem',
    color: '#fff',
    fontSize: '0.85rem',
    fontWeight: 600,
    textAlign: 'center',
    justifyContent: 'center',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#fff',
    opacity: 0.9,
  },
}
